import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Typography, 
  Box, 
  AppBar, 
  Toolbar, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Avatar, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
  TextField,
  Paper,
  Alert,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';

import { 
  Menu as MenuIcon, 
  Notifications, 
  AccountCircle,
  Dashboard as DashboardIcon, 
  Call as CallIcon, 
  PhoneCallback as PhoneCallbackIcon,
  BarChart as AnalyticsIcon,
  Settings as SettingsIcon,
  Report as ReportIcon,
  ExitToApp as LogoutIcon,
  PhoneForwarded as OutboundIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  Error as ErrorIcon,
  HelpOutline as DefaultIcon,
  Event as ScheduledIcon,
  CloudUpload as UploadIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Science as TestIcon,
  Feedback as FeedbackIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  BatchPrediction as BatchesIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService, callsService, organizationService } from '../utils/api';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';



function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState(null);
  
  // Get user role for conditional rendering
  const userRole = authService.getUserRole();
  const canViewCosts = userRole === 'super_admin' || userRole === 'admin';
  
  // Real data state for stats
  const [stats, setStats] = useState({
    totalCalls: 0,
    totalDuration: 0,
    totalCost: 0,
    averageDuration: 0,
    averageCostPerMinute: 0
  });

  // Recent call logs state
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentCallsLoading, setRecentCallsLoading] = useState(false);
  const [recentCallsError, setRecentCallsError] = useState(null);

  const currencySymbol = process.env.REACT_APP_CURRENCY_SYMBOL || '';

  // Call scheduler state
  const [selectedFile, setSelectedFile] = useState(null);
  const [earliestCallTime, setEarliestCallTime] = useState('');
  const [latestCallTime, setLatestCallTime] = useState('');
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerMessage, setSchedulerMessage] = useState('');
  const [schedulerError, setSchedulerError] = useState('');

  // Date filter state for analytics
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Load current organization on component mount
  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
  }, []);

  // Fetch recent call logs (just first 5 calls)
  const fetchRecentCalls = async () => {
    try {
      setRecentCallsLoading(true);
      const logsData = await callsService.getCallLogs(1, 5); // Only first 5 calls
      setRecentCalls(logsData.result || []);
      setRecentCallsError(null);
    } catch (err) {
      console.error('Error fetching recent calls:', err);
      setRecentCallsError('Failed to load recent calls');
    } finally {
      setRecentCallsLoading(false);
    }
  };

  // Fetch analytics and call logs on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch analytics with date filters
        const analyticsData = await callsService.getCallAnalytics(
          fromDate || null,
          toDate || null
        );
        setStats({
          totalCalls: analyticsData.totalCalls || 0,
          totalDuration: analyticsData.totalDuration || 0,
          totalCost: analyticsData.totalCost || 0,
          averageDuration: analyticsData.averageDuration || 0,
          averageCostPerMinute: analyticsData.averageCostPerMinute || 0
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchRecentCalls(); // Fetch recent call logs
  }, []);

  // Utility functions
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatDuration = (minutes) => {
    const totalMinutes = Math.floor(minutes);
    const seconds = Math.round((minutes - totalMinutes) * 60);
    return `${totalMinutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined || isNaN(Number(cost))) return 'N/A';
    return `${currencySymbol}${Number(cost).toFixed(2)}`;
  };

  const getStatusColor = (status, endedReason) => {
    if (status === 'ended') {
      if (endedReason === 'customer-ended-call' || endedReason === 'assistant-ended-call') {
        return 'success.main';
      }
      return 'error.main';
    }
    if (status === 'scheduled') {
      return '#f3a82b';
    }
    return 'text.secondary';
  };

  const getStatusIcon = (status, endedReason) => {
    if (status === 'ended') {
      if (endedReason === 'customer-ended-call' || endedReason === 'assistant-ended-call') {
        return <SuccessIcon fontSize="small" />;
      }
      return <ErrorIcon fontSize="small" />;
    }
    if (status === 'scheduled') {
      return <ScheduledIcon fontSize="small" />;
    }
    return <DefaultIcon fontSize="small" />;
  };

  const getStatusText = (status, endedReason) => {
    if (status === 'ended') {
      if (endedReason === 'customer-ended-call') return 'Successful';
      if (endedReason === 'assistant-ended-call') return 'Successful';
      if (endedReason === 'customer-did-not-answer') return 'No Answer';
      if (endedReason === 'customer-busy') return 'Busy';
      return 'Failed';
    }
    if (status === 'scheduled') {
      return 'Scheduled';
    }
    return status || 'Unknown';
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setSchedulerError('Please select a CSV or Excel file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSchedulerError('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
      setSchedulerError('');
    }
  };

  const convertISTToGMT = (istDateTime) => {
    // Convert IST datetime to GMT format expected by API
    // JavaScript automatically handles timezone conversion when creating Date object
    // and .toISOString() returns GMT time
    const date = new Date(istDateTime);
    return date.toISOString().slice(0, 19); // Remove the 'Z' to get YYYY-MM-DDTHH:mm:ss format
  };

  const handleScheduleCalls = async () => {
    if (!selectedFile || !earliestCallTime || !latestCallTime) {
      setSchedulerError('Please fill all required fields');
      return;
    }

    if (new Date(latestCallTime) <= new Date(earliestCallTime)) {
      setSchedulerError('Latest call time must be after earliest call time');
      return;
    }

    try {
      setSchedulerLoading(true);
      setSchedulerError('');
      
      const earliestGMT = convertISTToGMT(earliestCallTime);
      const latestGMT = convertISTToGMT(latestCallTime);
      
      const response = await callsService.scheduleCallBatch(
        earliestGMT,
        latestGMT,
        selectedFile
      );
      
      setSchedulerMessage(response.message || 'Call batch scheduled successfully!');
      // Reset form
      setSelectedFile(null);
      setEarliestCallTime('');
      setLatestCallTime('');
      // Reset file input
      const fileInput = document.getElementById('excel-file-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      setSchedulerError(error.message || 'Failed to schedule call batch');
    } finally {
      setSchedulerLoading(false);
    }
  };

  const handleCloseSchedulerMessage = () => {
    setSchedulerMessage('');
  };

  const handleCloseSchedulerError = () => {
    setSchedulerError('');
  };

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...((canViewCosts || userRole === 'moderator') && currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }] : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator' ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const drawerWidth = 240;

  const drawer = (
    <Box sx={{ 
      width: drawerWidth, 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
      borderRight: '1px solid rgba(102, 117, 140, 0.12)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        pt: '12px',
        pb: '12px',
        px: 2,
        borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
        background: '#2541b2',
        color: 'white',
        position: 'relative'
      }}>
        {/* Side-by-side logos */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box
            component="img"
            src="/telly_logo.png"
            alt="TellyFlow"
            sx={{
              width: 'auto',
              height: '32px',
              objectFit: 'contain'
            }}
          />
          <Box sx={{ 
            width: '1px', 
            height: '24px', 
            backgroundColor: 'rgba(255, 255, 255, 0.3)' 
          }} />
          <Box
            sx={{
              width: '60px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {COMPANY_LOGO && (
              <Box
                component="img"
                src={COMPANY_LOGO}
                alt={`${COMPANY_NAME || 'Company'} Logo`}
                sx={{
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {drawerItems.map((item) => {
            const isActive = item.text === 'Dashboard';
            return (
              <ListItem 
                button 
                key={item.text} 
                onClick={() => navigate(item.path)}
                sx={{ 
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': {
                    transform: 'translateX(1px)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'all 0.2s ease-in-out'
                  }} 
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(102, 117, 140, 0.08)',
        background: 'rgba(102, 117, 140, 0.02)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(239, 71, 111, 0.08)',
              transform: 'translateX(2px)',
              boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)'
            },
            '&:active': {
              transform: 'translateX(1px)'
            }
          }}
        >
          <ListItemIcon 
            sx={{ 
              minWidth: 40,
              color: 'text.secondary',
              transition: 'color 0.2s ease-in-out',
              '.MuiListItem-root:hover &': {
                color: 'error.main'
              }
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              transition: 'color 0.2s ease-in-out'
            }} 
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Sidebar drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen && !isMobile ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{
            borderBottom: '1px solid rgba(102, 117, 140, 0.12)',
            backgroundColor: 'background.paper',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Toolbar sx={{ py: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{ 
                mr: 2,
                '&:hover': {
                  backgroundColor: 'rgba(67, 97, 238, 0.08)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }}>
              Dashboard
            </Typography>
            
            {/* Organization Switcher Button */}
            {currentOrganization && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchOrganization}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(67, 97, 238, 0.3)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(67, 97, 238, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                {currentOrganization.name}
              </Button>
            )}
            
            <IconButton 
              color="inherit" 
              onClick={handleOpenMenu}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(67, 97, 238, 0.08)'
                }
              }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                  border: '1px solid rgba(102, 117, 140, 0.12)'
                }
              }}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Dashboard Content */}
        <Container maxWidth="xl" sx={{ mt: 1.5, mb: 4, px: { xs: 1.5, sm: 2.5 } }}>
          {/* Welcome section */}
          <Box sx={{ 
            mb: 2.5, 
            p: { xs: 2, sm: 2.5 },
            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.06) 0%, rgba(67, 97, 238, 0.02) 100%)',
            borderRadius: 2,
            border: '1px solid rgba(67, 97, 238, 0.12)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #4361ee 0%, #738bff 100%)'
            }
          }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ 
              fontWeight: 700, 
              color: 'text.primary',
              mb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}>
              Welcome to {COMPANY_NAME ? `${COMPANY_NAME} ` : ''}Tellyflow
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.5,
              maxWidth: '500px'
            }}>
              Monitor your AI calling agents and their performance on a single dashboard
            </Typography>
            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </Box>

          {/* Date Filter */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 3, 
            flexWrap: 'wrap',
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Filter by Date:
            </Typography>
            <TextField
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <TextField
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const fetchData = async () => {
                  try {
                    setLoading(true);
                    const analyticsData = await callsService.getCallAnalytics(
                      fromDate || null,
                      toDate || null
                    );
                    setStats({
                      totalCalls: analyticsData.totalCalls || 0,
                      totalDuration: analyticsData.totalDuration || 0,
                      totalCost: analyticsData.totalCost || 0,
                      averageDuration: analyticsData.averageDuration || 0,
                      averageCostPerMinute: analyticsData.averageCostPerMinute || 0
                    });
                    setError(null);
                  } catch (err) {
                    console.error('Error fetching analytics:', err);
                    setError('Failed to load analytics data');
                  } finally {
                    setLoading(false);
                  }
                };
                fetchData();
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Apply Filter
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setFromDate('');
                setToDate('');
                const fetchData = async () => {
                  try {
                    setLoading(true);
                    const analyticsData = await callsService.getCallAnalytics(null, null);
                    setStats({
                      totalCalls: analyticsData.totalCalls || 0,
                      totalDuration: analyticsData.totalDuration || 0,
                      totalCost: analyticsData.totalCost || 0,
                      averageDuration: analyticsData.averageDuration || 0,
                      averageCostPerMinute: analyticsData.averageCostPerMinute || 0
                    });
                    setError(null);
                  } catch (err) {
                    console.error('Error fetching analytics:', err);
                    setError('Failed to load analytics data');
                  } finally {
                    setLoading(false);
                  }
                };
                fetchData();
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Clear
            </Button>
          </Box>

          {/* Loading state */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress size={40} />
            </Box>
          )}

          {/* Stats cards */}
          {!loading && (
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} lg={2.4}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid rgba(102, 117, 140, 0.12)',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 8px 24px rgba(67, 97, 238, 0.12)',
                    borderColor: 'rgba(67, 97, 238, 0.25)'
                  }
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        lineHeight: 1.3
                      }}>
                        Total <br />Calls
                      </Typography>
                      <Avatar sx={{ 
                        bgcolor: 'primary.main', 
                        width: { xs: 40, sm: 44 }, 
                        height: { xs: 40, sm: 44 },
                        boxShadow: '0 3px 8px rgba(67, 97, 238, 0.25)'
                      }}>
                        <OutboundIcon fontSize="small" />
                      </Avatar>
                    </Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: { xs: '1.5rem', sm: '1.75rem' },
                      color: 'text.primary'
                    }}>
                      {stats.totalCalls}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      All time calls made
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} lg={2.4}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid rgba(102, 117, 140, 0.12)',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 8px 24px rgba(23, 162, 184, 0.12)',
                    borderColor: 'rgba(23, 162, 184, 0.25)'
                  }
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        lineHeight: 1.3
                      }}>
                        Total<br />Duration
                      </Typography>
                      <Avatar sx={{ 
                        bgcolor: 'info.main', 
                        width: { xs: 40, sm: 44 }, 
                        height: { xs: 40, sm: 44 },
                        boxShadow: '0 3px 8px rgba(23, 162, 184, 0.25)'
                      }}>
                        <CallIcon fontSize="small" />
                      </Avatar>
                    </Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: { xs: '1.5rem', sm: '1.75rem' },
                      color: 'text.primary'
                    }}>
                      {stats.totalDuration.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      Minutes spent on calls
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} lg={2.4}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid rgba(102, 117, 140, 0.12)',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 8px 24px rgba(6, 214, 160, 0.12)',
                    borderColor: 'rgba(6, 214, 160, 0.25)'
                  }
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        lineHeight: 1.3
                      }}>
                        Average<br />Duration
                      </Typography>
                      <Avatar sx={{ 
                        bgcolor: 'success.main', 
                        width: { xs: 40, sm: 44 }, 
                        height: { xs: 40, sm: 44 },
                        boxShadow: '0 3px 8px rgba(6, 214, 160, 0.25)'
                      }}>
                        <PhoneCallbackIcon fontSize="small" />
                      </Avatar>
                    </Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: { xs: '1.5rem', sm: '1.75rem' },
                      color: 'text.primary'
                    }}>
                      {stats.averageDuration.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      Minutes per call
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Cost-related cards - only show for super_admin and admin */}
              {canViewCosts && (
                <>
                  <Grid item xs={12} sm={6} lg={2.4}>
                    <Card sx={{ 
                      height: '100%',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(102, 117, 140, 0.12)',
                      borderRadius: 2,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 8px 24px rgba(247, 37, 133, 0.12)',
                        borderColor: 'rgba(247, 37, 133, 0.25)'
                      }
                    }}>
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ 
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            lineHeight: 1.3
                          }}>
                            Total<br />Cost
                          </Typography>
                          <Avatar sx={{ 
                            bgcolor: 'secondary.main', 
                            width: { xs: 40, sm: 44 }, 
                            height: { xs: 40, sm: 44 },
                            boxShadow: '0 3px 8px rgba(247, 37, 133, 0.25)'
                          }}>
                            <AnalyticsIcon fontSize="small" />
                          </Avatar>
                        </Box>
                        <Typography variant="h3" sx={{ 
                          fontWeight: 700, 
                          mb: 0.5,
                          fontSize: { xs: '1.5rem', sm: '1.75rem' },
                          color: 'text.primary'
                        }}>
                          {formatCost(stats.totalCost)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          fontSize: '0.7rem',
                          fontWeight: 500
                        }}>
                          Total spend on calls
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={2.4}>
                    <Card sx={{ 
                      height: '100%',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(102, 117, 140, 0.12)',
                      borderRadius: 2,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 8px 24px rgba(255, 193, 102, 0.12)',
                        borderColor: 'rgba(255, 193, 102, 0.25)'
                      }
                    }}>
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ 
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            lineHeight: 1.3
                          }}>
                            Avg Cost<br />Per Minute
                          </Typography>
                          <Avatar sx={{ 
                            bgcolor: 'warning.main', 
                            width: { xs: 40, sm: 44 }, 
                            height: { xs: 40, sm: 44 },
                            boxShadow: '0 3px 8px rgba(255, 193, 102, 0.25)'
                          }}>
                            <AnalyticsIcon fontSize="small" />
                          </Avatar>
                        </Box>
                        <Typography variant="h3" sx={{ 
                          fontWeight: 700, 
                          mb: 0.5,
                          fontSize: { xs: '1.5rem', sm: '1.75rem' },
                          color: 'text.primary'
                        }}>
                          {formatCost(stats.averageCostPerMinute)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          fontSize: '0.7rem',
                          fontWeight: 500
                        }}>
                          Cost per minute
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {/* Recent Calls Table */}
          {!loading && (
            <Card sx={{ 
              border: '1px solid rgba(102, 117, 140, 0.12)',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)'
            }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  p: { xs: 2, sm: 2.5 },
                  borderBottom: '1px solid rgba(102, 117, 140, 0.12)',
                  background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.02) 0%, rgba(67, 97, 238, 0.01) 100%)'
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: { xs: '1.125rem', sm: '1.25rem' }
                  }}>
                    Recent Calls
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mt: 0.5,
                    fontSize: '0.8rem'
                  }}>
                    Latest call activities and performance metrics
                  </Typography>
                </Box>
                
                <Box sx={{ overflow: 'auto' }}>
                  <Table sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow sx={{ 
                        '& th': { 
                          borderBottom: '2px solid rgba(102, 117, 140, 0.12)',
                          backgroundColor: 'rgba(67, 97, 238, 0.02)',
                          py: { xs: 1.5, sm: 2 },
                          px: { xs: 1.5, sm: 2 }
                        }
                      }}>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: 'primary.main',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Phone Number
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: 'primary.main',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Duration
                        </TableCell>
                        {canViewCosts && (
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'primary.main',
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Cost
                          </TableCell>
                        )}
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: 'primary.main',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: 'primary.main',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Created At
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentCalls.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canViewCosts ? 5 : 4} sx={{ 
                            textAlign: 'center', 
                            py: 4,
                            color: 'text.secondary'
                          }}>
                            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, fontSize: '0.9rem' }}>
                              No calls found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              Your recent calls will appear here once you start making calls
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentCalls.map((call, index) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'rgba(67, 97, 238, 0.02)',
                                '& td': {
                                  borderColor: 'rgba(67, 97, 238, 0.1)'
                                }
                              },
                              '&:last-child td': { 
                                borderBottom: 0 
                              },
                              '& td': {
                                py: { xs: 1.5, sm: 2 },
                                px: { xs: 1.5, sm: 2 },
                                borderBottom: '1px solid rgba(102, 117, 140, 0.08)'
                              }
                            }}
                          >
                            <TableCell sx={{ 
                              fontWeight: 400,
                              fontSize: '0.8rem',
                              color: 'text.primary'
                            }}>
                              {call.phone_number}
                            </TableCell>
                            <TableCell sx={{ 
                              fontWeight: 400,
                              fontSize: '0.8rem',
                              color: 'text.primary'
                            }}>
                              {call.minutes !== null && call.minutes !== undefined ? formatDuration(call.minutes) : 'N/A'}
                            </TableCell>
                            {canViewCosts && (
                              <TableCell sx={{ 
                                fontWeight: 400,
                                fontSize: '0.8rem',
                                color: 'text.primary'
                              }}>
                                {call.cost !== null && call.cost !== undefined ? formatCost(call.cost) : 'N/A'}
                              </TableCell>
                            )}
                            <TableCell>
                              <Chip 
                                label={getStatusText(call.status, call.ended_reason)} 
                                size="small"
                                variant="filled"
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  height: 28,
                                  px: 1.5,
                                  borderRadius: 1.5,
                                  textTransform: 'capitalize',
                                  letterSpacing: '0.25px',
                                  ...(call.status === 'ended' && (call.ended_reason === 'customer-ended-call' || call.ended_reason === 'assistant-ended-call') ? {
                                    backgroundColor: '#e8f5e8',
                                    color: '#2e7d32',
                                    border: '1px solid #c8e6c9',
                                    '& .MuiChip-label': {
                                      color: '#2e7d32'
                                    }
                                  } : call.status === 'ended' ? {
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    border: '1px solid #ffcdd2',
                                    '& .MuiChip-label': {
                                      color: '#c62828'
                                    }
                                  } : call.status === 'scheduled' ? {
                                    backgroundColor: '#fff3e0',
                                    color: '#f57c00',
                                    border: '1px solid #ffcc02',
                                    '& .MuiChip-label': {
                                      color: '#f57c00'
                                    }
                                  } : {
                                    backgroundColor: '#f5f5f5',
                                    color: '#616161',
                                    border: '1px solid #e0e0e0',
                                    '& .MuiChip-label': {
                                      color: '#616161'
                                    }
                                  })
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ 
                              fontWeight: 400,
                              fontSize: '0.8rem',
                              color: 'text.secondary'
                            }}>
                              {new Date(call.createdAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </Box>
  );
}

export default Dashboard; 